import { continueSubscription, createMembership } from '@/services/account';
import { useMobxStores } from '@/stores';
import AuthStore from '@/stores/AuthStore';
import { IMembershipResponse } from '@/types';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Modal from 'antd/lib/modal/Modal';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { modalType } from './Modal';

export interface IMembershipCardData {
  createSubscription: boolean;
  showCard: boolean;
  client_secret: string;
  customer_id: string;
}

const do_membership = async (
  stripeToken: string,
  email: string
): Promise<IMembershipResponse> => {
  const data = await createMembership(stripeToken, email);

  if (data.status === 'OK') {
    return data;
  } else {
    if (data.status == 'NOK' && data.ex) {
      modalType({
        type: 'error',
        title: 'error',
        content:
          'The following errors were found when trying to process your membership: ' +
          data.ex.split('|')[1],
      });
    } else {
      modalType({
        type: 'error',
        title: 'error',
        content:
          'The following errors were found when trying to process your membership: ' +
          data,
      });
    }
  }

  return data;
};

const MembershipCardModal = ({
  ui,
  uiSet,
}: {
  ui: IMembershipCardData;
  uiSet: Function;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { authStore }: { authStore: AuthStore } = useMobxStores();
  const [errors, setErrors] = useState<string>('');
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const continueMembership = async (e): Promise<void> => {
    e.preventDefault();
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);

    setConfirmLoading(true);
    if (ui.client_secret) {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        ui.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name:
                'MEMBERFEESUB: ' +
                authStore.user?.member_login +
                ' at ' +
                dayjs().format('MM/DD/YYYY HH:mm:ss'),
            },
          },
        }
      );
      const ret = await continueSubscription(
        authStore.user?.member_login,
        ui.client_secret
      );
      setConfirmLoading(false);
      if (ret.status == 'OK') {
        authStore.assignJWT(ret.jwt);
        uiSet({
          ...ui,
          showCard: false,
          client_secret: null,
          customer_id: null,
        });
      } else {
        setErrors('Error finishing subscription, contact support');
      }
      return;
    }
    // Use your card Element with other Stripe.js APIs
    const result = await stripe.createToken(cardElement);
    if (result.error) {
      // Inform the customer that there was an error
      setConfirmLoading(false);
      setErrors(result.error.message);
      //    $("#btn-reserve").attr("disabled", false);
    } else {
      // Send the token to your server
      const ret = await do_membership(
        result.token.id,
        authStore.user?.member_login
      );
      if (ret.status === 'OK') {
        authStore.assignJWT(ret.jwt);
        uiSet({ ...ui, showCard: false });
      }
      setConfirmLoading(false);
    }
  };
  return (
    <Modal
      visible={ui.showCard}
      title="BUY A MEMBERSHIP"
      onOk={continueMembership}
      closable={!confirmLoading}
      keyboard={false}
      onCancel={() => {
        if (confirmLoading) return false;
        uiSet({ ...ui, showCard: false });
      }}
      confirmLoading={confirmLoading}
    >
      <CardElement
        className={`border rounded-md pl-4 py-4`}
        options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
        }}
      />
      {errors && <span>Errors: {errors}</span>}
    </Modal>
  );
};

export default observer(MembershipCardModal);
